import { IconDeposit, IconDoor, IconNavHome, IconNavPrizes, IconNavProfile, IconNavRefer } from "@assets";
import CoinHeartbeat from "@components/utils/CoinHeartbeat";
import { useDepositDrawer } from "@components/utils/DepositDrawer/hooks";
import MemberModeProtector from "@components/utils/MemberModeProtector";
import { openOnboardingDialog } from "@components/utils/OnboardingDialog/utils";
import { AuthSource, DepositSource, usePrizesWordingExperiment, useStatsigTracking } from "@configs/statsig";
import { OnboardMode, Paths } from "@constants";
import useCSCompensation from "@hooks/useCSCompensation";
import { useMegaShareStatus } from "@hooks/useMegaShareStatus";
import usePromotionStatus from "@hooks/usePromotionStatus";
import useSkinnerBoxStatus from "@hooks/useSkinnerBoxStatus";
import { UserMode } from "@types";
import { getThemeAsset } from "@utils/theme";
import { MegaShareState } from "@views/PromotionMegaSharePage/types";
import {
  BottomNavigationAction,
  BottomNavigation as BottomNavigationUIKit,
  PropsWithClassName,
  WarningWrapper,
  cn,
} from "kz-ui-sdk";
import { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

interface BottomNavigationProps extends PropsWithChildren, PropsWithClassName {
  userMode: UserMode;
}

const BottomNavigation = ({ className, userMode }: BottomNavigationProps) => {
  const { t } = useTranslation();
  const { trackRegisterClick } = useStatsigTracking();
  const { openDepositDrawer } = useDepositDrawer();

  const { isAnyClaimable: isPromotionsClaimable, isReferralClaimable, isGiverSecretCodeUnseen } = usePromotionStatus();

  const { shouldShowSkinnerBox, skinnerBoxClaimableAmount } = useSkinnerBoxStatus();
  const { shouldShowCSCompensation } = useCSCompensation();
  const {
    shouldShowTakerMegaShare,
    taker: megaShareTaker,
    details: megaShareDetails,
    isGiverMegaShareUnseen,
  } = useMegaShareStatus();

  const { prizesLabelKey } = usePrizesWordingExperiment();

  const megaShareStatus = megaShareTaker?.status;
  const megaShareClaimAmount = megaShareDetails?.takerClaimAmount;

  const shouldShowPromotionAlerts =
    ((shouldShowSkinnerBox && skinnerBoxClaimableAmount > 0) ||
      shouldShowCSCompensation ||
      (shouldShowTakerMegaShare &&
        megaShareStatus === MegaShareState.CLAIMABLE &&
        !!megaShareClaimAmount &&
        megaShareClaimAmount > 0)) ??
    false;

  const shouldShowPrizeAlert = isPromotionsClaimable || isGiverMegaShareUnseen || isGiverSecretCodeUnseen;

  const handleCenterButtonClick = () => {
    if (userMode === UserMode.MEMBER) {
      // Track deposit click from bottom navigation (tracking happens automatically in openDepositDrawer)
      openDepositDrawer({ source: DepositSource.BOTTOM_NAVIGATION });
    } else {
      // Track registration click from bottom navigation
      trackRegisterClick(AuthSource.BOTTOM_NAVIGATION_CENTER_BUTTON);
      openOnboardingDialog({
        mode: OnboardMode.REGISTER,
      });
    }
  };

  const mainButtonLabel = `Bottom navigation ${userMode === UserMode.MEMBER ? "Deposit" : "Register"}`;

  return (
    <>
      <BottomNavigationUIKit className={cn(className)}>
        {/*HOME*/}
        <NavLink
          to={Paths.PRIVATE.HOME}
          className="h-[56px] w-[56px] flex-shrink-0"
        >
          {({ isActive }) => (
            <BottomNavigationAction
              icon={
                <WarningWrapper
                  isShown={shouldShowPromotionAlerts}
                  classes={{
                    "span&": "right-[-4px] top-[-2px] !h-[16px] !w-[16px] !text-[10px] border-2 border-[#291c5a]",
                  }}
                  icon={
                    <CoinHeartbeat
                      className="absolute -right-1 -top-1"
                      imgClassName="h-[13px] w-[13px]"
                      aria-label="Bottom Navigate Home"
                    />
                  }
                >
                  <IconNavHome
                    className={isActive ? "text-content-base" : "text-content-soft"}
                    width={20}
                    height={20}
                    aria-label="Bottom navigation Home"
                  />
                </WarningWrapper>
              }
              label={t("Home")}
              active={isActive}
              className="w-full"
              aria-label="Bottom navigation Home"
            />
          )}
        </NavLink>

        {/*PRIZES*/}
        <NavLink
          to={Paths.PRIVATE.PRIZES}
          className="h-[56px] w-[56px] flex-shrink-0"
        >
          {({ isActive }) => (
            <>
              <BottomNavigationAction
                icon={
                  <WarningWrapper
                    isShown={shouldShowPrizeAlert ?? false}
                    classes={{
                      "span&": "right-[-4px] top-[-2px] !h-[16px] !w-[16px] !text-[10px] border-2 border-[#291c5a]",
                    }}
                    icon={
                      <CoinHeartbeat
                        className="absolute -right-1 -top-1"
                        imgClassName="h-[13px] w-[13px]"
                        aria-label="Bottom navigation Prizes"
                      />
                    }
                  >
                    <IconNavPrizes
                      width={20}
                      height={20}
                      className={isActive ? "text-content-base" : "text-content-soft"}
                      aria-label="Bottom navigation Prizes"
                    />
                  </WarningWrapper>
                }
                label={t(prizesLabelKey)}
                className="h-[56px] w-[56px]"
                active={isActive}
                aria-label="Bottom navigation Prizes"
              />
            </>
          )}
        </NavLink>

        {/*CENTER BUTTON*/}
        <button
          onClick={handleCenterButtonClick}
          className="group inline-block h-[60px] w-[60px] translate-y-[-10px]"
          aria-label={mainButtonLabel}
        >
          <div
            className={cn(
              "relative h-full w-full rounded-full bg-bottomNavBtnRound shadow-[0px_4px_6px_0px_rgba(0,_0,_0,_0.24)] transition-transform will-change-transform active:translate-y-0.5",
            )}
          >
            {/*COLOR MIST BG*/}
            <div
              className="absolute left-0 top-0 h-full w-full animate-spin rounded-full will-change-transform [animation-duration:16s]"
              style={{
                backgroundImage: `url(${getThemeAsset("misc/bg-nav-center.png")})`,
              }}
            />

            {/*ICON & TEXT*/}
            <div className="absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center">
              {/*DEPOSIT*/}
              {userMode === UserMode.MEMBER && (
                <>
                  <IconDeposit
                    width={20}
                    height={20}
                    className="text-content-base"
                    aria-label={mainButtonLabel}
                  />
                  <span
                    className="text-xs font-bold text-content-base"
                    aria-label={mainButtonLabel}
                  >
                    {t("Deposit")}
                  </span>
                </>
              )}
              {/*REGISTER*/}
              {userMode === UserMode.GUEST && (
                <>
                  <IconDoor
                    width={20}
                    height={20}
                    className="text-content-base"
                    aria-label={mainButtonLabel}
                  />
                  <span
                    className="text-xs font-bold text-content-base"
                    aria-label={mainButtonLabel}
                  >
                    {t("nav register")}
                  </span>
                </>
              )}
            </div>

            {/*RIM LIGHT*/}
            <div
              className={`absolute left-0 top-0 h-full w-full rounded-full border-0 border-dashed border-[rgba(0,0,0,0.20)] bg-bottomNavBtnRoundRimLight shadow-[inset_0px_2px_1px_0px_rgba(255,255,255,0.24),_inset_0px_4px_4px_0px_rgba(255,255,255,0.25)]`}
              aria-label={mainButtonLabel}
            />
            {/*GRAY OVERLAY*/}
            <div
              className={`absolute left-0 top-0 h-full w-full rounded-full bg-[rgba(217,217,217,0.01)]`}
              aria-label={mainButtonLabel}
            />
          </div>
        </button>

        {/*REFER*/}
        <NavLink
          to={Paths.PRIVATE.PROMOTION_REFER_FRIEND}
          className="h-[56px] w-[56px] flex-shrink-0"
        >
          {({ isActive }) => (
            <>
              <BottomNavigationAction
                icon={
                  <WarningWrapper
                    isShown={isReferralClaimable ?? false}
                    classes={{
                      "span&": "right-[-4px] top-[-2px] !h-[16px] !w-[16px] !text-[10px] border-2 border-[#291c5a]",
                    }}
                    icon={
                      <CoinHeartbeat
                        className="absolute -right-1 -top-1"
                        imgClassName="h-[13px] w-[13px]"
                        aria-label="Bottom navigation Refer"
                      />
                    }
                  >
                    <IconNavRefer
                      width={20}
                      height={20}
                      className={isActive ? "text-content-base" : "text-content-soft"}
                      aria-label="Bottom navigation Refer"
                    />
                  </WarningWrapper>
                }
                label={t("Refer")}
                className="h-[56px] w-[56px]"
                active={isActive}
                classes={{
                  "label&": "whitespace-nowrap",
                }}
                aria-label="Bottom navigation Refer"
              />
            </>
          )}
        </NavLink>

        {/*PROFILE*/}
        <MemberModeProtector registrationSource={AuthSource.BOTTOM_NAVIGATION_PROFILE_BUTTON}>
          <NavLink
            to={Paths.PRIVATE.PROFILE}
            className="h-[56px] w-[56px] flex-shrink-0"
          >
            {({ isActive }) => (
              <BottomNavigationAction
                icon={
                  <IconNavProfile
                    width={22}
                    height={22}
                    className={isActive ? "text-content-base" : "text-content-soft"}
                    aria-label="Bottom navigation Profile"
                  />
                }
                label={t("Profile")}
                active={isActive}
                className="w-full"
                aria-label="Bottom navigation Profile"
              />
            )}
          </NavLink>
        </MemberModeProtector>
      </BottomNavigationUIKit>
    </>
  );
};

export default BottomNavigation;
